import { useState, useMemo, useCallback } from "react";

export type ViewMode = "manual" | "rolling" | "yoy";

interface PersistedFilters {
  startDate: string;
  endDate: string;
  selectedTeams: string[];
  selectedSprints: string[];
  viewMode: ViewMode;
}

export interface AiMetricsFilters {
  startDate: Date;
  endDate: Date;
  selectedTeams: string[];
  selectedSprints: string[];
  viewMode: ViewMode;
  currentPeriod: { start: Date; end: Date };
  priorPeriod: { start: Date; end: Date };
  comparisonEnabled: boolean;
  pickersReadOnly: boolean;
  comparisonLabel: string;
  hasPersistedFilters: boolean;
  setStartDate: (d: Date) => void;
  setEndDate: (d: Date) => void;
  setSelectedTeams: (teams: string[]) => void;
  setSelectedSprints: (sprints: string[]) => void;
  setViewMode: (mode: ViewMode) => void;
}

const VALID_VIEW_MODES: ViewMode[] = ["manual", "rolling", "yoy"];

// Legacy sentinel value from a prior "All Area Paths" implementation that
// mixed it into selectedTeams. Some browsers may still have it persisted.
const LEGACY_PROJECT_WIDE_SENTINEL = "__project_wide__";

function loadPersistedFilters(
  storageKey: string,
): Partial<PersistedFilters> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<PersistedFilters>;
  } catch {
    return null;
  }
}

function persistFilters(storageKey: string, filters: PersistedFilters): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(filters));
  } catch {
    /* storage full – silently ignore */
  }
}

function formatMonthRange(start: Date, end: Date): string {
  const fmt = (d: Date) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    return `${day} ${month} ${d.getFullYear()}`;
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function isValidDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
}

export const useAiMetricsFilters = (storageKey: string): AiMetricsFilters => {
  const saved = useMemo(() => loadPersistedFilters(storageKey), [storageKey]);

  const [startDate, setStartDateRaw] = useState<Date>(() => {
    if (saved && isValidDate(saved.startDate)) return new Date(saved.startDate);
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth() - 3, 1);
  });
  const [endDate, setEndDateRaw] = useState<Date>(() => {
    if (saved && isValidDate(saved.endDate)) return new Date(saved.endDate);
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  });
  const [selectedTeams, setSelectedTeamsRaw] = useState<string[]>(() =>
    saved && Array.isArray(saved.selectedTeams)
      ? saved.selectedTeams.filter(
          (team) => team !== LEGACY_PROJECT_WIDE_SENTINEL,
        )
      : [],
  );
  const [selectedSprints, setSelectedSprintsRaw] = useState<string[]>(() =>
    saved && Array.isArray(saved.selectedSprints) ? saved.selectedSprints : [],
  );
  const [viewMode, setViewModeRaw] = useState<ViewMode>(() =>
    saved && VALID_VIEW_MODES.includes(saved.viewMode as ViewMode)
      ? (saved.viewMode as ViewMode)
      : "manual",
  );

  const sync = useCallback(
    (patch: Partial<PersistedFilters>) => {
      const current: PersistedFilters = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        selectedTeams,
        selectedSprints,
        viewMode,
        ...patch,
      };
      persistFilters(storageKey, current);
    },
    [storageKey, startDate, endDate, selectedTeams, selectedSprints, viewMode],
  );

  const setStartDate = useCallback(
    (d: Date) => {
      setStartDateRaw(d);
      sync({ startDate: d.toISOString() });
    },
    [sync],
  );

  const setEndDate = useCallback(
    (d: Date) => {
      setEndDateRaw(d);
      sync({ endDate: d.toISOString() });
    },
    [sync],
  );

  const setSelectedTeams = useCallback(
    (teams: string[]) => {
      setSelectedTeamsRaw(teams);
      sync({ selectedTeams: teams });
    },
    [sync],
  );

  const setSelectedSprints = useCallback(
    (sprints: string[]) => {
      setSelectedSprintsRaw(sprints);
      sync({ selectedSprints: sprints });
    },
    [sync],
  );

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeRaw(mode);
      sync({ viewMode: mode });
    },
    [sync],
  );

  const currentPeriod = useMemo(() => {
    if (viewMode === "rolling") {
      const now = new Date();
      const start = new Date(
        now.getFullYear(),
        now.getMonth() - 3,
        now.getDate(),
      );
      return { start, end: now };
    }
    return { start: startDate, end: endDate };
  }, [startDate, endDate, viewMode]);

  const priorPeriod = useMemo(() => {
    if (viewMode === "yoy") {
      const priorStart = new Date(currentPeriod.start);
      priorStart.setFullYear(currentPeriod.start.getFullYear() - 1);
      const priorEnd = new Date(currentPeriod.end);
      priorEnd.setFullYear(currentPeriod.end.getFullYear() - 1);
      return { start: priorStart, end: priorEnd };
    }
    const priorEnd = new Date(currentPeriod.start.getTime() - 86400000);
    const priorStart = new Date(
      priorEnd.getTime() -
        (currentPeriod.end.getTime() - currentPeriod.start.getTime()),
    );
    return { start: priorStart, end: priorEnd };
  }, [currentPeriod, viewMode]);

  const comparisonEnabled = viewMode !== "manual";
  const pickersReadOnly = viewMode === "rolling";

  const comparisonLabel = useMemo(() => {
    if (!comparisonEnabled) {
      return formatMonthRange(currentPeriod.start, currentPeriod.end);
    }
    return `${formatMonthRange(currentPeriod.start, currentPeriod.end)} vs ${formatMonthRange(priorPeriod.start, priorPeriod.end)}`;
  }, [currentPeriod, priorPeriod, comparisonEnabled]);

  return {
    startDate,
    endDate,
    selectedTeams,
    selectedSprints,
    viewMode,
    currentPeriod,
    priorPeriod,
    comparisonEnabled,
    pickersReadOnly,
    comparisonLabel,
    hasPersistedFilters: saved !== null,
    setStartDate,
    setEndDate,
    setSelectedTeams,
    setSelectedSprints,
    setViewMode,
  };
};
